/**
 * AppShell · layout post-login : sidebar + topbar + content.
 * Style DA Victor identique au site vitrine.
 */
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { motion, AnimatePresence } from "motion/react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthContext";
import { ImpersonationBanner } from "../../auth/ImpersonationBanner";
import { Logo } from "../../ui/Logo";
import { Avatar } from "../../ui/Avatar";
import { GlobalSearch } from "./GlobalSearch";
import { CommandPalette } from "./CommandPalette";
import { ConseillerChatWidget } from "../support/ConseillerChatWidget";
import { useConseillerChatOpen } from "../../lib/conseillerChat";
import { NotificationBell } from "./NotificationBell";
import { ThemeToggle } from "./ThemeToggle";
import { OnboardingWizard } from "../onboarding/OnboardingWizard";
import { api } from "../../lib/api";
import { trackPageview, setTenantGroup } from "../../lib/analytics";
import { ACTIVITY_BY_ROUTE } from "../../auth/activityGating";
import { startProductTour, hasSeenTour } from "./productTour";
import { DemoBanner } from "./DemoBanner";
import { useDemoMode, launchDemo } from "../../lib/demo/demoMode";

type NavEntry = {
  to: string;
  label: string;
  icon: React.ReactNode;
};

const NAV: NavEntry[] = [
  {
    to: "/",
    label: "Tableau de bord",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
      </svg>
    ),
  },
  {
    to: "/planning",
    label: "Planning",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
  },
  {
    to: "/pointages",
    label: "Pointages",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },
  {
    to: "/main-courante",
    label: "Main courante",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    to: "/rondes",
    label: "Rondes",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="3 11 22 2 13 21 11 13 3 11" />
      </svg>
    ),
  },
  {
    to: "/alertes",
    label: "Alertes",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
  {
    to: "/utilisateurs",
    label: "Utilisateurs",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    to: "/etablissements",
    label: "Établissements",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 21h18" />
        <path d="M5 21V7l8-4v18" />
        <path d="M19 21V11l-6-4" />
      </svg>
    ),
  },
  {
    to: "/sites",
    label: "Sites client",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  {
    to: "/agents",
    label: "Agents",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 11l-3 3-2-2" />
      </svg>
    ),
  },
  {
    to: "/conges",
    label: "Congés",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
  },
  {
    to: "/prepaie",
    label: "Prépaie",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 10h12" />
        <path d="M4 14h9" />
        <path d="M19 6a7.7 7.7 0 0 0-5.2-2A7.9 7.9 0 0 0 6 12c0 4.4 3.5 8 7.8 8 2 0 3.8-.8 5.2-2" />
      </svg>
    ),
  },
  {
    to: "/cynophile",
    label: "Cynophile",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10 5.172C10 3.422 8.578 2 6.828 2 5.343 2 4.137 3.044 3.85 4.443a2.05 2.05 0 0 1-1.4 1.405A2 2 0 0 0 1 7.762v1.476a2 2 0 0 0 2 2h2.59c.79 0 1.575.3 2.18.886l2.21 2.232a2 2 0 0 0 2.84-2.82l-1.92-1.92a3 3 0 0 1-.9-2.15Z" />
        <path d="M14 5.172c0-1.75 1.422-3.172 3.172-3.172 1.485 0 2.691 1.044 2.978 2.443a2.05 2.05 0 0 0 1.4 1.405A2 2 0 0 1 23 7.762v1.476a2 2 0 0 1-2 2h-2.59c-.79 0-1.575.3-2.18.886l-2.21 2.232a2 2 0 0 1-2.84-2.82l1.92-1.92a3 3 0 0 0 .9-2.15Z" />
        <path d="M8 14v2a4 4 0 0 0 8 0v-2" />
      </svg>
    ),
  },
  {
    to: "/ssiap",
    label: "SSIAP",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
      </svg>
    ),
  },
  {
    to: "/telesurveillance",
    label: "Télésurveillance",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      </svg>
    ),
  },
  {
    to: "/evenementiel",
    label: "Événementiel",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 11l18-5v12L3 14v-3z" />
        <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
      </svg>
    ),
  },
  {
    to: "/opportunites",
    label: "Opportunités",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
  },
  {
    to: "/reporting",
    label: "Reporting",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 3v18h18" />
        <path d="M7 14l4-4 4 4 5-6" />
      </svg>
    ),
  },
  {
    to: "/materiel",
    label: "Matériel",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
  },
  {
    to: "/sous-traitance",
    label: "Sous-traitance",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="8.5" cy="7" r="4" />
        <path d="M20 8v6" />
        <path d="M23 11h-6" />
      </svg>
    ),
  },
  {
    to: "/clients",
    label: "Clients",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      </svg>
    ),
  },
  {
    to: "/contrats",
    label: "Contrats",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="15" y2="17" />
      </svg>
    ),
  },
  {
    to: "/commercial",
    label: "Commercial",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 10h12" />
        <path d="M4 14h9" />
        <path d="M19 6a7.7 7.7 0 0 0-5.2-2A7.9 7.9 0 0 0 6 12c0 4.4 3.5 8 7.8 8 2 0 3.8-.8 5.2-2" />
      </svg>
    ),
  },
  {
    to: "/documents",
    label: "Documents",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    ),
  },
  {
    to: "/audit",
    label: "Journal d'audit",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    to: "/import-export",
    label: "Import / Export",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="7 10 12 5 17 10" />
        <line x1="12" y1="5" x2="12" y2="15" />
        <line x1="3" y1="20" x2="21" y2="20" />
      </svg>
    ),
  },
  {
    to: "/rgpd",
    label: "RGPD",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
    ),
  },
  {
    to: "/rapports-incident",
    label: "Rapports d'incident",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <circle cx="12" cy="17" r="1" />
      </svg>
    ),
  },
  {
    to: "/anomalies",
    label: "Anomalies",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  },
  {
    to: "/astreintes",
    label: "Astreintes",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="13" r="8" />
        <path d="M12 9v4l2 2" />
        <path d="M5 3 2 6" />
        <path d="m22 6-3-3" />
      </svg>
    ),
  },
  {
    to: "/items-sensibles",
    label: "Items sensibles",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    to: "/visites-controle",
    label: "Contrôles CNAPS",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <polyline points="9 14 11 16 15 12" />
      </svg>
    ),
  },
  {
    to: "/webhooks",
    label: "Webhooks",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 12a4 4 0 1 1 8 0v0" />
        <path d="m15 18-3 3-3-3" />
        <path d="M12 14v7" />
      </svg>
    ),
  },
];

/**
 * Regroupement de la sidebar par sections métier. `title: null` = bloc de
 * tête sans intitulé. Toute entrée de NAV absente des groupes est ajoutée
 * automatiquement en fin de sidebar (filet pour les prochains modules).
 */
const NAV_GROUPS: Array<{
  title: string | null;
  icon?: React.ReactNode;
  routes: string[];
}> = [
  { title: null, routes: ["/", "/reporting"] },
  {
    title: "Exploitation",
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    routes: [
      "/planning",
      "/pointages",
      "/main-courante",
      "/rondes",
      "/alertes",
      "/astreintes",
      "/rapports-incident",
      "/anomalies",
      "/items-sensibles",
      "/telesurveillance",
      "/evenementiel",
    ],
  },
  {
    title: "Mes agents",
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    routes: ["/agents", "/conges", "/prepaie", "/cynophile", "/ssiap"],
  },
  {
    title: "Commercial",
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
    routes: [
      "/clients",
      "/sites",
      "/contrats",
      "/commercial",
      "/opportunites",
      "/sous-traitance",
    ],
  },
  {
    title: "Entreprise",
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
        <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
        <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
        <path d="M10 6h4" />
        <path d="M10 10h4" />
        <path d="M10 14h4" />
        <path d="M10 18h4" />
      </svg>
    ),
    routes: [
      "/etablissements",
      "/utilisateurs",
      "/materiel",
      "/documents",
      "/visites-controle",
      "/import-export",
      "/rgpd",
      "/audit",
      "/webhooks",
    ],
  },
];

export function AppShell() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const { demoRequired } = useAuth();
  const demoActive = useDemoMode();

  // Analytics : $pageview à chaque changement de route (SPA).
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);

  // Onboarding : une fois le contrat signé (AppShell ne se monte qu'une fois
  // les gates levés), on montre la démo aux NOUVEAUX clients (demoRequired),
  // une seule fois par navigateur. Sinon, simple visite guidée à la 1ʳᵉ fois.
  useEffect(() => {
    let justStartedDemo = false;
    try {
      justStartedDemo = sessionStorage.getItem("victor.demo.justStarted") === "1";
      if (justStartedDemo) sessionStorage.removeItem("victor.demo.justStarted");
    } catch {
      /* stockage indisponible */
    }
    if (demoActive) {
      if (!justStartedDemo) return; // déjà en démo, ne pas relancer à chaque nav
      const t = setTimeout(() => startProductTour(), 900);
      return () => clearTimeout(t);
    }
    // Nouveau client (contrat signé) → on lance la démo automatiquement, 1 fois.
    let autoShown = true;
    try {
      autoShown = localStorage.getItem("victor.demo.autoshown") === "1";
    } catch {
      /* stockage indisponible */
    }
    if (demoRequired && !autoShown) {
      try {
        localStorage.setItem("victor.demo.autoshown", "1");
      } catch {
        /* stockage indisponible */
      }
      launchDemo();
      return;
    }
    if (hasSeenTour()) return;
    const t = setTimeout(() => startProductTour(), 900);
    return () => clearTimeout(t);
  }, [demoRequired, demoActive]);

  return (
    <div className="flex min-h-screen flex-col">
      <ImpersonationBanner />
      {demoActive ? <DemoBanner /> : null}
      <div className="flex flex-1">
      {/* Sidebar desktop */}
      <Sidebar className="hidden lg:flex" />

      {/* Sidebar mobile (drawer) */}
      <AnimatePresence>
        {mobileNavOpen ? (
          <motion.div
            className="fixed inset-0 z-40 flex lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-navy-900/40 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileNavOpen(false)}
            />
            <motion.div
              initial={{ x: -260 }}
              animate={{ x: 0 }}
              exit={{ x: -260 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative z-10"
            >
              <Sidebar onItemClick={() => setMobileNavOpen(false)} />
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onMenuClick={() => setMobileNavOpen(true)} />
        <main className="flex-1 bg-navy-50/40">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
      </div>
      <FloatingHelp />
      <ConseillerChatWidget />
      <CommandPalette />
      <OnboardingWizard />
    </div>
  );
}

/** Bouton d'aide flottant (bas-droite), masqué sur la page d'aide elle-même. */
/** Total des messages non lus (réponses conseiller) sur les tickets du tenant. */
function useSupportUnread(): number {
  const q = useQuery({
    queryKey: ["support-tickets"],
    queryFn: () => api.support.list(),
    refetchInterval: 60_000,
  });
  return (q.data ?? []).reduce((s, t) => s + (t.unread ?? 0), 0);
}

/** Mappe la page courante vers une catégorie d'aide (aide contextuelle). */
const HELP_BY_ROUTE: Record<string, string> = {
  "/planning": "planning",
  "/planning-calendrier": "planning",
  "/planning-templates": "planning",
  "/agents": "agents",
  "/conges": "agents",
  "/recrutement": "agents",
  "/clients": "commercial",
  "/contrats": "commercial",
  "/commercial": "commercial",
  "/boutique": "boutique",
  "/abonnement": "boutique",
  "/pointages": "agent-pwa",
  "/main-courante": "agent-pwa",
  "/rondes": "agent-pwa",
  "/mobile": "agent-pwa",
  "/items-sensibles": "cnaps",
  "/rapports-incident": "cnaps",
  "/mon-compte": "compte",
  "/parametres": "compte",
};

function helpHref(pathname: string): string {
  const key = Object.keys(HELP_BY_ROUTE).find((r) => pathname.startsWith(r));
  return key ? `/help?cat=${HELP_BY_ROUTE[key]}` : "/help";
}

function FloatingHelp() {
  const location = useLocation();
  const chatOpen = useConseillerChatOpen();
  const unread = useSupportUnread();
  if (chatOpen) return null;
  if (location.pathname.startsWith("/help")) return null;
  return (
    <Link
      to={helpHref(location.pathname)}
      aria-label="Aide"
      className="group fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-navy-800 py-3 pl-4 pr-5 text-sm font-semibold text-white shadow-xl shadow-navy-800/25 transition-all hover:-translate-y-0.5 hover:bg-navy-700"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      Aide
      {unread > 0 ? (
        <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1.5 text-[11px] font-bold text-white ring-2 ring-white">
          {unread}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * Routes accessibles à un user CLI (portail client donneur d'ordre).
 * Le filtrage applicatif côté API garantit déjà la sécurité (404 hors
 * scope) ; ce set sert juste à masquer les entrées de navigation
 * non pertinentes pour un donneur d'ordre.
 */
const CLI_ALLOWED_ROUTES = new Set<string>([
  "/",
  "/sites",
  "/planning",
  "/main-courante",
  "/rondes",
  "/commercial",
]);

/** Clé localStorage de l'état plié/déplié des sections de la sidebar. */
const NAV_SECTIONS_LS_KEY = "victor.nav.sections";

function loadSectionState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(NAV_SECTIONS_LS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function Sidebar({
  className = "",
  onItemClick,
}: {
  className?: string;
  onItemClick?: () => void;
}) {
  const { roles, cli, activites } = useAuth();
  const isCli = roles.includes("CLI") && !!cli;
  const location = useLocation();

  // Branding du tenant : logo + nom affiché en tête de sidebar. Les users
  // CLI (portail donneur d'ordre) gardent le logo Victor.
  const brandingQ = useQuery({
    queryKey: ["branding"],
    queryFn: () => api.branding.get(),
    enabled: !isCli,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const tenantQ = useQuery({
    queryKey: ["tenant"],
    queryFn: () => api.tenants.me(),
    enabled: !isCli,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const tenantLogo = !isCli ? (brandingQ.data?.logoUrl ?? null) : null;
  const logoSeul = brandingQ.data?.logoSeul ?? false;
  const agenceNom =
    brandingQ.data?.nomAffiche?.trim() ||
    tenantQ.data?.raisonSociale ||
    "";

  // Analytics : enrichit le groupe tenant (nom, plan) pour l'admin.
  useEffect(() => {
    if (tenantQ.data) {
      setTenantGroup(tenantQ.data.id, {
        name: tenantQ.data.raisonSociale,
        plan: tenantQ.data.plan,
        statut: tenantQ.data.statut,
      });
    }
  }, [tenantQ.data]);

  // Mémorise le branding pour l'écran de connexion (affiché avant auth,
  // donc lu depuis le localStorage du poste).
  useEffect(() => {
    if (isCli || !brandingQ.data) return;
    try {
      if (brandingQ.data.logoUrl) {
        localStorage.setItem(
          "victor.login.branding",
          JSON.stringify({ logoUrl: brandingQ.data.logoUrl, nom: agenceNom }),
        );
      } else {
        localStorage.removeItem("victor.login.branding");
      }
    } catch {
      // stockage indisponible : l'écran de connexion restera Victor
    }
  }, [isCli, brandingQ.data, agenceNom]);
  const [sectionState, setSectionState] = useState<Record<string, boolean>>(
    loadSectionState,
  );

  function toggleSection(title: string, current: boolean) {
    setSectionState((prev) => {
      const next = { ...prev, [title]: !current };
      try {
        localStorage.setItem(NAV_SECTIONS_LS_KEY, JSON.stringify(next));
      } catch {
        // stockage indisponible : l'état reste en mémoire pour la session
      }
      return next;
    });
  }
  // Outils métier spécifiques à une activité : affichés seulement si l'agence
  // a déclaré l'activité à la souscription. activites vide = tout débloqué
  // (tenants existants / rétro-compat).
  const passesActivity = (route: string) => {
    const act = ACTIVITY_BY_ROUTE[route];
    if (!act) return true; // cœur (toujours visible)
    if (!activites || activites.length === 0) return true;
    return activites.includes(act);
  };
  const visibleNav = (
    isCli ? NAV.filter((item) => CLI_ALLOWED_ROUTES.has(item.to)) : NAV
  ).filter((item) => passesActivity(item.to));

  // Construit les sections depuis NAV_GROUPS ; les entrées non listées
  // tombent dans "Autres", les sections vides (filtrage CLI) sont masquées.
  const byRoute = new Map(visibleNav.map((item) => [item.to, item]));
  const listedRoutes = new Set(NAV_GROUPS.flatMap((g) => g.routes));
  const sections = NAV_GROUPS.map((g) => ({
    title: g.title,
    icon: g.icon ?? null,
    items: g.routes
      .map((r) => byRoute.get(r))
      .filter((item): item is NavEntry => Boolean(item)),
  }));
  const orphans = visibleNav.filter((item) => !listedRoutes.has(item.to));
  if (orphans.length > 0) {
    sections.push({
      title: "Autres",
      icon: (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="5" cy="12" r="1" />
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
        </svg>
      ),
      items: orphans,
    });
  }
  const visibleSections = sections.filter((sec) => sec.items.length > 0);

  // Dépliées par défaut : Exploitation, Mes agents et la section contenant
  // la route active. Le choix explicite de l'utilisateur (localStorage)
  // prime toujours.
  const DEFAULT_OPEN = ["Exploitation", "Mes agents"];
  const isRouteActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
  const activeSectionTitle =
    visibleSections.find((sec) => sec.items.some((it) => isRouteActive(it.to)))
      ?.title ?? null;
  const isSectionOpen = (title: string) =>
    sectionState[title] ??
    (DEFAULT_OPEN.includes(title) || title === activeSectionTitle);

  return (
    <aside
      data-tour="sidebar"
      className={`sticky top-0 flex h-screen w-64 shrink-0 flex-col border-r border-navy-100 bg-white ${className}`}
    >
      <div className="flex h-16 items-center px-5">
        <Link
          to="/"
          className="flex min-w-0 items-center gap-2.5"
          onClick={onItemClick}
        >
          {tenantLogo ? (
            logoSeul ? (
              <img
                src={tenantLogo}
                alt={agenceNom || "Logo"}
                className="logo-agence h-11 w-auto max-w-[185px] object-contain"
              />
            ) : (
              <>
                <img
                  src={tenantLogo}
                  alt={agenceNom || "Logo"}
                  className="logo-agence h-9 w-auto max-w-[44px] shrink-0 object-contain"
                />
                <span className="truncate text-[15px] font-bold text-navy-900">
                  {agenceNom}
                </span>
              </>
            )
          ) : (
            <Logo size={32} withWordmark />
          )}
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-5 [scrollbar-width:thin]">
        {/* S2 · Bandeau portail client si user CLI */}
        {cli ? (
          <div className="mb-4 rounded-xl bg-accent-50 px-3 py-2 ring-1 ring-accent-100">
            <p className="text-[10px] font-bold uppercase tracking-wider text-accent-700">
              Portail client
            </p>
            <p className="text-sm font-bold text-navy-800 truncate">
              {cli.clientRaisonSociale}
            </p>
          </div>
        ) : null}

        {visibleSections.map((section, i) => {
          const open = section.title ? isSectionOpen(section.title) : true;
          const sectionHasActive = section.items.some((it) =>
            isRouteActive(it.to),
          );
          return (
            <div key={section.title ?? "_accueil"}>
              {section.title ? (
                <button
                  type="button"
                  onClick={() => toggleSection(section.title as string, open)}
                  aria-expanded={open}
                  className={`flex w-full items-center justify-between rounded-lg px-3 pb-2 text-[13px] font-semibold transition-colors hover:text-navy-800 ${
                    i > 0 ? "pt-7" : "pt-1"
                  } ${sectionHasActive && !open ? "text-accent-600" : "text-navy-500"}`}
                >
                  <span className="flex items-center gap-2">
                    {section.icon}
                    {section.title}
                  </span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              ) : null}
              <AnimatePresence initial={false}>
                {open ? (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                    className="overflow-hidden"
                  >
                    <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === "/"}
                        onClick={onItemClick}
                        className={({ isActive }) =>
                          `flex items-center gap-3.5 rounded-xl px-3 py-2.5 text-[15px] transition-colors ${
                            isActive
                              ? "bg-navy-50 font-semibold text-navy-900"
                              : "font-medium text-navy-700 hover:bg-navy-50/60 hover:text-navy-900"
                          }`
                        }
                      >
                        <span className="shrink-0 [&>svg]:h-5 [&>svg]:w-5">{item.icon}</span>
                        <span className="flex-1">{item.label}</span>
                        {item.to === "/alertes" ? <AlertesBadge /> : null}
                        {item.to === "/agents" ? <QualifsExpirantesBadge /> : null}
                      </NavLink>
                    </li>
                      ))}
                    </ul>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          );
        })}
      </nav>

      {tenantLogo ? (
        <div className="border-t border-navy-100 p-3">
          <a
            href="https://myvictor.fr"
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold text-navy-400 transition-opacity hover:opacity-80"
          >
            <span>Propulsé par</span>
            <Logo size={18} withWordmark />
          </a>
        </div>
      ) : null}
    </aside>
  );
}

function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  const { user, logout, roles, cli } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const demoActive = useDemoMode();
  const supportUnread = useSupportUnread();
  // Les users CLI (portail donneur d'ordre) ne voient ni la boutique ni la
  // gestion du compte agence (abonnement / paramètres).
  const isCli = roles.includes("CLI") && !!cli;

  async function handleLogout() {
    setMenuOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }

  const displayName = user ? `${user.prenom} ${user.nom}` : "-";

  return (
    <header data-tour="topbar" className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-navy-100 bg-white/85 px-4 backdrop-blur sm:px-6">
      {/* Menu mobile + recherche */}
      <div className="flex flex-1 items-center gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-navy-700 hover:bg-navy-50 lg:hidden"
          aria-label="Ouvrir le menu"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <line x1="4" y1="8" x2="20" y2="8" />
            <line x1="4" y1="16" x2="20" y2="16" />
          </svg>
        </button>

        <GlobalSearch />
      </div>

      {!isCli ? (
        <Link
          to="/boutique"
          className="inline-flex h-9 items-center gap-2 rounded-full px-3 text-sm font-semibold text-navy-700 transition-colors hover:bg-navy-100"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
          <span className="hidden sm:inline">Boutique</span>
        </Link>
      ) : null}
      <button
        type="button"
        onClick={() => startProductTour()}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-navy-700 transition-colors hover:bg-navy-100"
        aria-label="Revoir la visite guidée"
        title="Revoir la visite guidée"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2.5-3 4" />
          <line x1="12" y1="17.5" x2="12.01" y2="17.5" />
        </svg>
      </button>
      <ThemeToggle />
      <NotificationBell />

      {/* User menu */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex items-center gap-3 rounded-full p-1 pr-3 transition-colors hover:bg-navy-50"
          aria-expanded={menuOpen}
        >
          <Avatar name={displayName} size="md" />
          <span className="hidden text-sm font-semibold text-navy-800 sm:inline">
            {displayName}
          </span>
        </button>

        <AnimatePresence>
          {menuOpen ? (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setMenuOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 top-full z-40 mt-2 w-60 rounded-2xl bg-white p-2 shadow-2xl shadow-navy-800/15 ring-1 ring-navy-100"
              >
                <div className="border-b border-navy-100 p-3">
                  <p className="text-sm font-bold text-navy-800">{displayName}</p>
                  <p className="text-xs text-navy-500">{user?.email}</p>
                </div>
                <Link
                  to="/mon-compte"
                  onClick={() => setMenuOpen(false)}
                  className="mt-1 block rounded-lg px-3 py-2 text-sm font-semibold text-navy-700 hover:bg-navy-50"
                >
                  Mon compte
                </Link>
                {!isCli ? (
                  <>
                    <Link
                      to="/abonnement"
                      onClick={() => setMenuOpen(false)}
                      className="mt-0.5 block rounded-lg px-3 py-2 text-sm font-semibold text-navy-700 hover:bg-navy-50"
                    >
                      Mon abonnement
                    </Link>
                    <Link
                      to="/parametres"
                      onClick={() => setMenuOpen(false)}
                      className="mt-0.5 block rounded-lg px-3 py-2 text-sm font-semibold text-navy-700 hover:bg-navy-50"
                    >
                      Paramètres
                    </Link>
                  </>
                ) : null}
                <Link
                  to="/support"
                  onClick={() => setMenuOpen(false)}
                  className="mt-0.5 flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-navy-700 hover:bg-navy-50"
                >
                  <span>Mes demandes</span>
                  {supportUnread > 0 ? (
                    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1.5 text-[11px] font-bold text-white">
                      {supportUnread}
                    </span>
                  ) : null}
                </Link>
                {!isCli && !demoActive ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      launchDemo();
                    }}
                    className="mt-0.5 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold text-accent-600 hover:bg-accent-50"
                  >
                    <span aria-hidden="true">✨</span>
                    Découvrir la démo
                  </button>
                ) : null}
                <div className="my-1 border-t border-navy-100" />
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-red-600 hover:bg-red-50"
                >
                  Se déconnecter
                </button>
              </motion.div>
            </>
          ) : null}
        </AnimatePresence>
      </div>
    </header>
  );
}

/**
 * Badge sidebar pour l'entrée "Alertes" · count total + couleur reflétant
 * la sévérité maximale (rouge si bloquantes, orange si critiques, jaune
 * si info). Refresh auto toutes les 5 min, stale 1 min.
 */
function AlertesBadge() {
  const { data } = useQuery({
    queryKey: ["alerts-badge"],
    queryFn: () => api.alerts.list({}),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  if (!data || data.counts.total === 0) return null;
  const { bloquante, critique, total } = data.counts;
  const color =
    bloquante > 0
      ? "bg-red-600"
      : critique > 0
        ? "bg-orange-500"
        : "bg-amber-400";
  return (
    <span
      aria-label={`${total} alerte${total > 1 ? "s" : ""} active${total > 1 ? "s" : ""}`}
      className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-black text-white ${color}`}
    >
      {total > 99 ? "99+" : total}
    </span>
  );
}

/**
 * Badge sidebar pour l'entrée "Agents" · count des qualifications
 * (carte pro CNAPS, SSIAP, SST…) qui expirent dans les 60 jours et qui
 * sont déjà expirées. Couleur reflétant le pire niveau (rouge si déjà
 * expirées, orange si dans 30 j, amber sinon).
 */
function QualifsExpirantesBadge() {
  const { data } = useQuery({
    queryKey: ["qualifs-expirantes-badge"],
    queryFn: () => api.qualifications.expiring(60),
    staleTime: 60_000,
    refetchInterval: 10 * 60_000,
  });
  if (!data || data.length === 0) return null;
  const now = Date.now();
  let dejaExpirees = 0;
  let urgentes = 0;
  for (const q of data) {
    if (!q.expireLe) continue;
    const diffJ = Math.floor(
      (new Date(q.expireLe).getTime() - now) / (1000 * 60 * 60 * 24),
    );
    if (diffJ < 0) dejaExpirees += 1;
    else if (diffJ <= 30) urgentes += 1;
  }
  const total = data.length;
  const color =
    dejaExpirees > 0
      ? "bg-red-600"
      : urgentes > 0
        ? "bg-orange-500"
        : "bg-amber-400";
  return (
    <span
      aria-label={`${total} qualification${total > 1 ? "s" : ""} expirante${total > 1 ? "s" : ""}`}
      className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-black text-white ${color}`}
    >
      {total > 99 ? "99+" : total}
    </span>
  );
}
